import { cva, type VariantProps } from 'class-variance-authority';

/**
 * 語義色彩 Token
 *
 * 使用方式：
 *   import { TEXT, BTN, BTN_ON_DARK, INPUT, STATUS } from '@/styles/tokens';
 *   <p className={TEXT.secondary}>說明文字</p>
 *   <button className={`${BTN.danger} px-3 py-1.5 rounded-lg text-sm`}>刪除</button>
 *
 * 規則說明：
 *   - TOKEN 只包含「顏色相關」的 class（text-*, bg-*, border-*, ring-*, placeholder-*, caret-*）
 *   - 尺寸、間距、字型、圓角等排版 class 仍在各元件自行撰寫
 *   - 禁止使用半透明文字色（text-* / N）作為主要可讀文字，只允許用在裝飾層或圖片浮層上
 *   - 禁止「半透明底色按鈕 + disabled:opacity-N」的組合（相乘後幾乎不可見）
 */

// ────────────────────────────────────────────────────────────
// 一、基礎文字色（一般介面背景：白色 / 淺灰 / gray-50 上）
// ────────────────────────────────────────────────────────────
export const TEXT = {
  /** 標題、主要內文 */
  primary:   'text-slate-900 dark:text-slate-100',
  /** 說明文字、次要標籤 */
  secondary: 'text-slate-600 dark:text-slate-400',
  /** 備註、時間戳、輔助提示 */
  muted:     'text-slate-400 dark:text-slate-500',
  /** 危險 / 錯誤 */
  danger:    'text-red-600 dark:text-red-400',
  /** 成功 / 完成 */
  success:   'text-emerald-600 dark:text-emerald-400',
  /** 警告 */
  warning:   'text-amber-600 dark:text-amber-400',
  /** 主題強調 / 連結 */
  accent:    'text-blue-600 dark:text-blue-400',
} as const;

// ────────────────────────────────────────────────────────────
// 二、深色遮罩 / Overlay 環境文字色
//     用於：Lightbox、全螢幕 Modal overlay、圖片浮層上方
//     ⚠ 此環境禁止使用 text-*/50 等半透明白字作為主要文字
// ────────────────────────────────────────────────────────────
export const TEXT_ON_DARK = {
  /** 主要文字（標題、按鈕標籤） */
  primary:   'text-white',
  /** 次要文字（副標題、說明） */
  secondary: 'text-slate-300',
  /** 輔助文字（時間、備註）— 仍比 text-white/50 更可靠 */
  muted:     'text-slate-400',
} as const;

// ────────────────────────────────────────────────────────────
// 三、按鈕 CVA（variant × context compound variants）
//     context: default = 一般介面  onDark = Lightbox/遮罩  onLight = 淺色卡片
//     ⚠ BTN_ON_DARK 在淺色模式下仍需 index.css 的 text-white 例外規則
// ────────────────────────────────────────────────────────────
export const buttonVariants = cva(
  'disabled:opacity-50 disabled:cursor-not-allowed transition-all',
  {
    variants: {
      variant: {
        primary: '',
        danger:  '',
        neutral: '',
        ghost:   '',
        success: '',
        warning: '',
      },
      context: {
        default: '',
        onDark:  '',
        onLight: '',
      },
    },
    compoundVariants: [
      // ── context: default ───────────────────────────────────
      { variant: 'primary', context: 'default',
        className: 'bg-blue-600 hover:bg-blue-500 text-white' },
      { variant: 'danger',  context: 'default',
        className: 'bg-red-500 hover:bg-red-600 text-white' },
      { variant: 'neutral', context: 'default',
        className: 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200' },
      { variant: 'ghost',   context: 'default',
        className: 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400' },
      { variant: 'success', context: 'default',
        className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
      { variant: 'warning', context: 'default',
        className: 'bg-amber-500 hover:bg-amber-600 text-white' },
      // ── context: onDark ─────────────────────────────────────
      { variant: 'primary', context: 'onDark',
        className: 'bg-white text-slate-950 border border-transparent hover:bg-slate-200 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 shadow-sm' },
      { variant: 'danger',  context: 'onDark',
        className: 'bg-red-600 text-white border border-transparent hover:bg-red-500 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 shadow-sm' },
      { variant: 'neutral', context: 'onDark',
        className: 'bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 hover:text-white focus-visible:ring-2 focus-visible:ring-slate-600 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 shadow-sm' },
      // ── context: onLight ────────────────────────────────────
      { variant: 'primary', context: 'onLight',
        className: 'bg-slate-900 text-white border border-transparent hover:bg-slate-800 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white shadow-sm' },
      { variant: 'danger',  context: 'onLight',
        className: 'bg-red-500 text-white border border-transparent hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white shadow-sm' },
      { variant: 'neutral', context: 'onLight',
        className: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white shadow-sm' },
    ],
    defaultVariants: {
      variant: 'neutral',
      context: 'default',
    },
  }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

/** 向後相容：現有程式碼 `BTN.primary` 仍可直接使用 */
export const BTN = {
  primary:  buttonVariants({ variant: 'primary',  context: 'default' }),
  danger:   buttonVariants({ variant: 'danger',   context: 'default' }),
  neutral:  buttonVariants({ variant: 'neutral',  context: 'default' }),
  ghost:    buttonVariants({ variant: 'ghost',    context: 'default' }),
  success:  buttonVariants({ variant: 'success',  context: 'default' }),
  warning:  buttonVariants({ variant: 'warning',  context: 'default' }),
} as const;

export const BTN_ON_DARK = {
  primary:  buttonVariants({ variant: 'primary',  context: 'onDark' }),
  danger:   buttonVariants({ variant: 'danger',   context: 'onDark' }),
  neutral:  buttonVariants({ variant: 'neutral',  context: 'onDark' }),
} as const;

export const BTN_ON_LIGHT = {
  primary:  buttonVariants({ variant: 'primary',  context: 'onLight' }),
  danger:   buttonVariants({ variant: 'danger',   context: 'onLight' }),
  neutral:  buttonVariants({ variant: 'neutral',  context: 'onLight' }),
} as const;

// ────────────────────────────────────────────────────────────
// 五、輸入框 CVA
// ────────────────────────────────────────────────────────────
export const inputVariants = cva(
  'focus:outline-none focus:ring-1',
  {
    variants: {
      variant: {
        base:   'border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-gray-100 focus:ring-blue-500',
        answer: 'border border-blue-300 dark:border-blue-700/60 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100 placeholder-blue-400/60 focus:ring-blue-400',
        label:  'border border-emerald-300 dark:border-emerald-700/60 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 dark:text-emerald-100 placeholder-emerald-500/50 focus:ring-emerald-400',
      },
    },
    defaultVariants: { variant: 'base' },
  }
);

export type InputVariants = VariantProps<typeof inputVariants>;

/** 向後相容 */
export const INPUT = {
  base:    inputVariants({ variant: 'base' }),
  answer:  inputVariants({ variant: 'answer' }),
  label:   inputVariants({ variant: 'label' }),
} as const;

// ────────────────────────────────────────────────────────────
// 六、狀態色塊 CVA（Banner、Alert、inline 訊息）
// ────────────────────────────────────────────────────────────
export const statusVariants = cva(
  'border',
  {
    variants: {
      variant: {
        error:   'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
        success: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
        warning: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700',
        info:    'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      },
    },
    defaultVariants: { variant: 'info' },
  }
);

export type StatusVariants = VariantProps<typeof statusVariants>;

/** 向後相容 */
export const STATUS = {
  error:   statusVariants({ variant: 'error' }),
  success: statusVariants({ variant: 'success' }),
  warning: statusVariants({ variant: 'warning' }),
  info:    statusVariants({ variant: 'info' }),
} as const;

// ────────────────────────────────────────────────────────────
// 七、色票 Chip CVA（行內標籤，用於語法高亮 / 選項卡片）
// ────────────────────────────────────────────────────────────
export const chipVariants = cva(
  'rounded',
  {
    variants: {
      variant: {
        answer: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
        label:  'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
      },
    },
  }
);

export type ChipVariants = VariantProps<typeof chipVariants>;

/** 向後相容 */
export const CHIP = {
  answer: chipVariants({ variant: 'answer' }),
  label:  chipVariants({ variant: 'label' }),
} as const;
